// Test procedures: acuity staircase, contrast size gate + Pelli-Robson (or QUEST).
// Pure logic: all timing and drawing go through a Presenter, so the procedures run the
// same in the browser (RafPresenter) and in tests (a simulated observer).

import { colourCodes, mixCodes, presentedRgb, type ColourCodes, type LetterRule, type RGB3 } from "../lib/display";
import {
  type AcuityConfig, type Appearance, type ContrastConfig, type EdgeJitter, type Orientation, type ResponseKey,
  type Timing, ORIENTATIONS, baseLetterLogmar, buildAcuityStaircase, contrastQuestParams,
  contrastStaircaseParams, prLevels, withStaircaseDefaults,
} from "../lib/protocol";
import type { EdgeJitterFrame } from "../render/stimuli/tumblingE";
import { choice, mulberry32 } from "../lib/rng";
import { DoubleStaircase, InterleavedStaircases } from "../lib/staircase";
import type { Geometry } from "../lib/units";

export type Phase = "main" | "catch" | "gate";

export interface TrialRecord {
  seq: number;
  phase: Phase;
  staircase: string;
  requested_value: number;
  presented_value: number;
  orientation: Orientation;
  response: ResponseKey;
  correct: boolean;
  rt_ms: number | null;
  level_index: number | null;
  weber: number | null;
  logmar: number | null;
  frame: FrameStats | null;
  /** Codes written to the framebuffer (0..1) and the same on a 0-1023 scale, for debugging. */
  bg_code: number | null;
  letter_code: number | null;
  bg_code10: number | null;
  letter_code10: number | null;
  stroke_px: number | null;
  letter_px: number | null;
  /** Per-channel codes on the 0-1023 scale (colour programs; equal channels on grey). */
  bg_rgb_code10?: number[] | null;
  letter_rgb_code10?: number[] | null;
  /** Contrast "toward the letter colour": the fraction of the colour difference drawn. */
  modulation?: number | null;
  /** Edge jitter: the mix weight of the blob colours actually used. */
  jitter_mix?: number | null;
}

export interface FrameStats {
  frames: number;
  median_ms: number;
  max_ms: number;
  dropped: number;
}

export interface Stimulus {
  orientation: Orientation;
  strokePx: number;
  backgroundCode: number;
  letterCode: number;
  backgroundRgb?: RGB3;
  letterRgb?: RGB3;
  jitter?: EdgeJitterFrame | null;
}

export interface PresentResult {
  response: ResponseKey;
  rtMs: number | null;
  frame: FrameStats | null;
}

export interface Presenter {
  present(stim: Stimulus, timing: Timing, signal: AbortSignal): Promise<PresentResult>;
  blank(backgroundCode: number, backgroundRgb?: RGB3): void;
}

export interface RunnerEnv {
  presenter: Presenter;
  geometry: Geometry;
  lut: ArrayLike<number>;
  outputBits: number;
  backgroundRel: number;
  /** Letter / background colours and the contrast mode (default: black on grey). */
  appearance?: Appearance;
  rng: () => number;
  signal: AbortSignal;
  submit: (t: TrialRecord) => void;
  progress?: (p: RunnerProgress) => void;
}

export interface RunnerProgress {
  seq: number;
  phase: Phase;
  value: number;
  correct: boolean;
  label: string;
  bg_code10: number;
  letter_code10: number;
  stroke_px: number;
}

export class AbortedError extends Error {
  constructor() {
    super("test aborted");
  }
}

// above the longest allowed run: 8 staircases x 60 trials + a 20-trial size gate
const MAX_TRIALS = 600;
const NO_CONTRAST_LOG_WEBER = -4.0;

export function backgroundOf(env: RunnerEnv): RGB3 {
  const bg = env.appearance?.background_rgb_rel;
  return bg ? [bg[0], bg[1], bg[2]] : [env.backgroundRel, env.backgroundRel, env.backgroundRel];
}

/** Codes for a letter at contrast `c`: the acuity letter is the letter colour itself
 *  ("fixed"); the contrast letter follows the program's contrast mode. */
export function codesFor(env: RunnerEnv, rule: LetterRule, c: number): ColourCodes {
  const letter = env.appearance?.letter_rgb_rel ?? [0, 0, 0];
  return colourCodes(env.lut, rule, backgroundOf(env), [letter[0], letter[1], letter[2]], c, env.outputBits);
}

function contrastRule(env: RunnerEnv): LetterRule {
  return env.appearance?.contrast_mode === "toward_letter" ? "toward_letter" : "luminance";
}

/** The faintest letter this display path can draw (one code step from the background),
 *  as log CS (or log modulation): staircases are not allowed to ask for less. */
export function drawableCeilingLogCs(env: Pick<RunnerEnv, "lut" | "outputBits" | "backgroundRel" | "appearance">): number {
  const rule = env.appearance?.contrast_mode === "toward_letter" ? "toward_letter" : "luminance";
  const shown = (logC: number) => presentedContrast(codesFor(env as RunnerEnv, rule, 10 ** logC), rule);
  let lo = -8;   // nothing drawn
  let hi = 0;    // full contrast
  if (shown(hi) <= 0) return 0;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (shown(mid) > 0) hi = mid;
    else lo = mid;
  }
  return -Math.log10(shown(hi));
}

/** The contrast measure presented: Weber luminance contrast, or the modulation depth. */
function presentedContrast(codes: ColourCodes, rule: LetterRule): number {
  return rule === "toward_letter" ? codes.presentedModulation ?? 0 : codes.presentedWeber;
}

/** Jitter band, blobs and mixed colours for one trial (null when off). */
export function jitterFrame(env: RunnerEnv, j: EdgeJitter | undefined, strokePx: number, codes: ColourCodes,
  contrast: number): { frame: EdgeJitterFrame; mix: number } | null {
  if (!j?.enabled) return null;
  const scale = j.unit === "stroke" ? strokePx : 1;
  const mix = j.intensity * (j.link_to_contrast ? Math.min(1, Math.max(0, contrast)) : 1);
  const bg = presentedRgb(env.lut, codes.backgroundRgb, env.outputBits);
  const letter = presentedRgb(env.lut, codes.letterRgb, env.outputBits);
  const c1 = j.color1_rgb_rel as RGB3;
  const c2 = j.color2_rgb_rel as RGB3;
  return {
    mix,
    frame: {
      innerPx: j.inner_width * scale, outerPx: j.outer_width * scale, blobPx: Math.max(1, j.blob_size * scale),
      density: j.density, updateFrames: j.update_frames,
      bgColour1: mixCodes(env.lut, bg, c1, mix), bgColour2: mixCodes(env.lut, bg, c2, mix),
      letterColour1: mixCodes(env.lut, letter, c1, mix), letterColour2: mixCodes(env.lut, letter, c2, mix),
    },
  };
}

const code10 = (rgb: RGB3) => rgb.map((v) => Math.round(v * 1023));

async function trial(
  env: RunnerEnv, seq: number, phase: Phase, label: string, requested: number, presented: number,
  strokePx: number, codes: ColourCodes, contrast: number, jitter: EdgeJitter | undefined, timing: Timing,
  extra: Partial<TrialRecord>,
): Promise<TrialRecord> {
  if (env.signal.aborted) throw new AbortedError();
  const orientation = choice(env.rng, ORIENTATIONS);
  const jf = jitterFrame(env, jitter, strokePx, codes, contrast);
  const r = await env.presenter.present(
    { orientation, strokePx, backgroundCode: codes.backgroundCode, letterCode: codes.letterCode,
      backgroundRgb: codes.backgroundRgb, letterRgb: codes.letterRgb, jitter: jf?.frame ?? null },
    timing, env.signal);
  if (env.signal.aborted) throw new AbortedError();
  const rec: TrialRecord = {
    seq, phase, staircase: label, requested_value: requested, presented_value: presented,
    orientation, response: r.response, correct: r.response === orientation, rt_ms: r.rtMs,
    level_index: null, weber: codes.presentedWeber, logmar: null, frame: r.frame,
    bg_code: codes.backgroundCode, letter_code: codes.letterCode,
    bg_code10: Math.round(codes.backgroundCode * 1023), letter_code10: Math.round(codes.letterCode * 1023),
    stroke_px: strokePx, letter_px: strokePx * 5,
    bg_rgb_code10: code10(codes.backgroundRgb), letter_rgb_code10: code10(codes.letterRgb),
    modulation: codes.presentedModulation, jitter_mix: jf ? Math.round(jf.mix * 1e4) / 1e4 : null, ...extra,
  };
  env.submit(rec);
  env.progress?.({ seq, phase, value: presented, correct: rec.correct, label,
    bg_code10: rec.bg_code10!, letter_code10: rec.letter_code10!, stroke_px: strokePx });
  return rec;
}

export interface AcuityOutcome {
  trials: number;
  estimateLogmar: number | null;
}

export async function runAcuity(cfg: AcuityConfig, env: RunnerEnv): Promise<AcuityOutcome> {
  const ds = buildAcuityStaircase(cfg, env.rng);
  const acuityCodes = codesFor(env, "fixed", 1.0);
  let seq = 0;
  while (!ds.isComplete && seq < MAX_TRIALS) {
    const isCatch = cfg.catch_rate > 0 && env.rng() < cfg.catch_rate;
    let requested: number;
    let label = "";
    let sc = null;
    if (isCatch) {
      requested = cfg.max_logmar;
    } else {
      const next = ds.nextTrial();
      requested = next.value;
      sc = next.sc;
      label = sc.label;
    }
    const snapped = env.geometry.snap(requested);
    const rec = await trial(env, seq, isCatch ? "catch" : "main", label, requested,
      snapped.presentedLogmar, snapped.strokePx, acuityCodes, 1.0, cfg.edge_jitter, cfg.timing,
      { logmar: snapped.presentedLogmar });
    if (sc) ds.feedResponse(sc, rec.correct, snapped.presentedLogmar);
    seq += 1;
  }
  return { trials: seq, estimateLogmar: ds.threshold() };
}

export interface ContrastOutcome {
  trials: number;
  letterLogmar: number | null;
  estimateLogCs: number | null;
}

export async function runContrast(
  cfg: ContrastConfig, acuityLogmar: number | null, env: RunnerEnv,
): Promise<ContrastOutcome> {
  let seq = 0;
  const rule = contrastRule(env);
  const fullCodes = codesFor(env, rule, 1.0);
  const maxSize = env.geometry.maxLogmar();
  let size = Math.min(baseLetterLogmar(cfg, acuityLogmar), maxSize);
  let passedSize: number | null = null;
  for (let attempt = 0; attempt < cfg.gate_max_attempts; attempt++) {
    const snapped = env.geometry.snap(size);
    let allOk = true;
    for (let k = 0; k < cfg.gate_trials; k++) {
      const rec = await trial(env, seq++, "gate", "", size, snapped.presentedLogmar, snapped.strokePx,
        fullCodes, presentedContrast(fullCodes, rule), cfg.edge_jitter, cfg.timing,
        { logmar: snapped.presentedLogmar });
      allOk = allOk && rec.correct;
    }
    if (allOk) {
      passedSize = snapped.presentedLogmar;
      break;
    }
    const next = Math.round((size + cfg.gate_step_logmar) * 1e6) / 1e6;
    if (next > maxSize + 1e-9) break;
    size = next;
  }
  if (passedSize === null) return { trials: seq, letterLogmar: null, estimateLogCs: null };
  const letter = env.geometry.snap(size);

  if (cfg.method === "staircases") {
    // Interleaved staircases: each trial goes to a running staircase picked at random; each
    // steps on this patient's right and wrong answers; the estimate is their mean.
    // The trial order (who joins when, which staircase, attention checks) comes from its own
    // seeded generator, recorded in the config, so the server can replay it exactly.
    const c = withStaircaseDefaults(cfg);
    const plannerRng = mulberry32(c.planner_seed ?? 0);
    const ms = new InterleavedStaircases(contrastStaircaseParams(c), plannerRng);
    while (!ms.isComplete && seq < MAX_TRIALS) {
      const plan = ms.planNext();
      const codes = codesFor(env, rule, 10 ** plan.value);
      const pc = presentedContrast(codes, rule);
      const presented = pc > 0 ? Math.log10(pc) : NO_CONTRAST_LOG_WEBER;
      const rec = await trial(env, seq++, plan.catch ? "catch" : "main", plan.sc?.label ?? "", plan.value,
        presented, letter.strokePx, codes, pc, cfg.edge_jitter, cfg.timing, { logmar: letter.presentedLogmar });
      if (plan.catch) ms.feedCatch(rec.correct);
      else ms.feedResponse(plan.sc, rec.correct, presented);
    }
    const t = ms.threshold();
    return { trials: seq, letterLogmar: passedSize, estimateLogCs: t === null ? null : -t };
  }

  if (cfg.method === "quest") {
    const ds = new DoubleStaircase(contrastQuestParams(cfg), env.rng);
    while (!ds.isComplete && seq < MAX_TRIALS) {
      const { value, sc } = ds.nextTrial();
      const codes = codesFor(env, rule, 10 ** value);
      const pc = presentedContrast(codes, rule);
      const presented = pc > 0 ? Math.log10(pc) : NO_CONTRAST_LOG_WEBER;
      const rec = await trial(env, seq++, "main", sc.label, value, presented, letter.strokePx,
        codes, pc, cfg.edge_jitter, cfg.timing, { logmar: letter.presentedLogmar });
      ds.feedResponse(sc, rec.correct, presented);
    }
    const t = ds.threshold();
    return { trials: seq, letterLogmar: passedSize, estimateLogCs: t === null ? null : -t };
  }

  const levels = prLevels(cfg);
  let lastPassed: number | null = null;
  for (let i = 0; i < levels.length; i++) {
    const level = levels[i]!;
    const codes = codesFor(env, rule, 10 ** -level);
    const pc = presentedContrast(codes, rule);
    const presented = pc > 0 ? -Math.log10(pc) : -NO_CONTRAST_LOG_WEBER;
    let nOk = 0;
    for (let k = 0; k < cfg.pr_group_size; k++) {
      const rec = await trial(env, seq++, "main", "", level, presented, letter.strokePx, codes, pc,
        cfg.edge_jitter, cfg.timing, { logmar: letter.presentedLogmar, level_index: i });
      if (rec.correct) nOk += 1;
    }
    if (nOk < cfg.pr_pass_correct) break;
    lastPassed = level;
  }
  return { trials: seq, letterLogmar: passedSize, estimateLogCs: lastPassed };
}
